import {applyFunctionsToScope} from "../../../util/scope";
import {applyCssToPage, loadExternalCss, loadExternalJavascript} from "../../../util/dom-helper";

import './details-slide.css';
import {arrayShallowEquality} from "../../../util/array-helper";

const SLIDE_DURATION = 800;

function closeSlide(contentArea, button = null) {
  contentArea.classList.remove("slide-open");
  if(button !== null) {
    button.classList.remove("active");
  }
}

function slideToggle(contentArea) {
  // to allow displaying old datasets that still have `display: none` in the content area
  contentArea.style.display = null;
  contentArea.classList.toggle("slide-open");
}

async function loadAndOpenSlide(contentArea, button, scope) {
  button.classList.toggle("active");
  const id = parseInt(button.dataset.id);
  if(contentArea === null) {
    console.warn("[details-slide] no details area found for id: " + id);
    return false;
  }
  const contentAreaVisible = contentArea.classList.contains("slide-open");
  const childIds = JSON.parse(button.dataset.childIds ?? "[]");
  const contentIds = JSON.parse(contentArea.dataset.contentIds ?? "[]");
  const closeOnly = button.dataset.closeOnly ?? false;

  if(contentAreaVisible && !arrayShallowEquality(childIds, contentIds) && !closeOnly) {
    closeSlide(contentArea, button);
    setTimeout(() => loadAndOpenSlide(contentArea, button, scope), SLIDE_DURATION);
    return;
  }

  const content = contentArea.innerHTML.trim();
  if(content.length > 10 && (arrayShallowEquality(childIds, contentIds) || contentAreaVisible)){
    slideToggle(contentArea);
    if(!contentAreaVisible) {
      contentArea.dataset.savedScrollingPosition = String(window.scrollY);

      contentArea.scrollIntoView({behavior: "smooth", block: "start"});
    } else if(typeof contentArea.dataset.savedScrollingPosition !== "undefined") {
      document.scrollingElement.scrollTo({top: parseInt(contentArea.dataset.savedScrollingPosition), behavior: "smooth"});
    }

    return;
  }
  try {
    const cssResponse = await fetch(`index.php?controller=dataset&function=show_css&ajax=1&id=${id}`, {
      method: 'GET',
    });
    if(!cssResponse.ok) {
      console.error("[details-slide] Error while loading detail content CSS: " + cssResponse.statusText);
      return;
    }

    const cssData = await cssResponse.text();
    applyCssToPage(cssData);

    const assetsResponse = await fetch(`index.php?controller=dataset&function=show_children_assets&ajax=1&id=${id}`, {
      method: 'GET',
    });
    if(!assetsResponse.ok) {
      console.error("[details-slide] Error while loading detail content assets: " + assetsResponse.statusText);
      return;
    }
    const files = await assetsResponse.json();
    const jsFiles = files.js ?? [];
    const cssFiles = files.css ?? [];
    jsFiles.forEach(file => loadExternalJavascript(file.path));
    cssFiles.forEach(file => loadExternalCss(file.path));

    let fetchUrl;
    if(childIds.length > 0) {
      const childIdsJson = JSON.stringify(childIds);
      const encodedChildIdsJson = encodeURIComponent(childIdsJson);
      fetchUrl = `index.php?controller=dataset&function=show_some_children&ajax=1&ids=${encodedChildIdsJson}&parent_id=${id}`;
    } else {
      fetchUrl = `index.php?controller=dataset&function=show_detail&ajax=1&id=${id}`;
    }

    const childrenResponse = await fetch(fetchUrl, {
      method: 'GET',
    });
    if(!childrenResponse.ok) {
      console.error("[details-slide] Error while loading detail content: " + childrenResponse.statusText);
      return;
    }
    const childrenHtml = await childrenResponse.text();
    if(typeof childIds !== "undefined") {
      contentArea.dataset.contentIds = JSON.stringify(childIds);
    }
    contentArea.innerHTML = '<ul>'+childrenHtml+'</ul>';
  } catch (e) {
    console.error("[details-slide] Error while loading detail content.", e);
    return;
  }

  const contentAreaScopes = Array.from(contentArea.children).map(childNode => Array.from(childNode.children)).flat();

  // rerun javascript on the content
  contentAreaScopes.forEach(scope => applyFunctionsToScope(scope));
  if(typeof window.legacyScopeJs === 'function') {
    contentAreaScopes.forEach(scope => window.legacyScopeJs(scope));
  }
  slideToggle(contentArea);

  if(!contentAreaVisible) {
    Array.from(contentArea.querySelectorAll(".details-slide-button")).forEach(
      button => button.classList.add("details-slide-scroll")
    );

    contentArea.dataset.savedScrollingPosition = String(window.scrollY);

    contentArea.scrollIntoView({behavior: "smooth", block: "start"});
  } else if(typeof contentArea.dataset.savedScrollingPosition !== "undefined") {
    document.scrollingElement.scrollTo({top: parseInt(contentArea.dataset.savedScrollingPosition), behavior: "smooth"});
  }
}

async function slideFunction(event, scope){
  event.preventDefault();
  const button = event.currentTarget;
  const id = button.dataset.id;
  const contentArea = document.querySelector("#details-content-" + id);
  if(contentArea === null) {
    console.warn("[details-slide] no details area found for id: " + id);
    return false;
  }
  const contentAreaVisible = contentArea.classList.contains('slide-open');
  if(!contentAreaVisible && (window.$global_config?.close_sliding_dataset_on_open ?? false)) {
    const contentElements = Array.from(scope.querySelectorAll(".details_content"));
    const visibleElements = contentElements.filter(content => content.classList.contains('slide-open'));
    const activeDetailsSlideButtons = Array.from(scope.querySelectorAll(".details-slide-button.active"));
    activeDetailsSlideButtons.forEach(button => button.classList.remove("active"));
    if(visibleElements.length > 0) {
      visibleElements.forEach(closeSlide);
      setTimeout(() => loadAndOpenSlide(contentArea, button, scope), SLIDE_DURATION);
    } else {
      await loadAndOpenSlide(contentArea, button, scope);
    }
  } else {
    await loadAndOpenSlide(contentArea, button, scope);
  }
  return false;
}

function setUpDetailsSlide(scope) {
  const detailsSlideButtons = Array.from(scope.querySelectorAll('.details-slide-button'));
  detailsSlideButtons.forEach(button => button.addEventListener('click', e => slideFunction(e, scope)));
}

export {setUpDetailsSlide, slideFunction};
